import { createNanoEvents, type Unsubscribe } from "nanoevents";

export type StorageKey = {
  version: string;
  name: string;
};

type StorageEvents<State> = {
  change: (key: StorageKey, state: State | null) => void;
};

export const createStorageDriver = (storage: Storage) => {
  const emitter = createNanoEvents<StorageEvents<any>>();
  const get = <V>(key: StorageKey): V | null => {
    const value = storage.getItem(key.name);
    if (!value) {
      return null;
    }
    try {
      const result = JSON.parse(value);
      if (result?.version !== key.version) {
        remove(key);
        emitter.emit("change", key, null);
        return null;
      }
      return result?.state;
    } catch (e) {
      // todo: log this
      return null;
    }
  };

  const set = <V>(key: StorageKey, value: V) => {
    storage.setItem(
      key.name,
      JSON.stringify({
        state: value,
        version: key.version,
      }),
    );
    emitter.emit("change", key, value);
  };

  const remove = (key: StorageKey) => {
    storage.removeItem(key.name);
    emitter.emit("change", key, null);
  };

  const on = <V, Key extends keyof StorageEvents<V> = keyof StorageEvents<V>>(
    name: Key,
    subscribed: StorageKey,
    cb: (value: V | null) => void,
  ): Unsubscribe => {
    return emitter.on(name, (key, value) => {
      if (subscribed.name === key.name) {
        cb(value);
      }
    });
  };

  return {
    get,
    set,
    remove,
    on,
  };
};

export type StorageDriver = ReturnType<typeof createStorageDriver>;
